// src/components/tilgjengelighet.js
// Felles hjelpere for universell utforming i kundeportalene.
// Se standarder/UNIVERSELL-UTFORMING.md (U1 sidetittel, U2 dialoger).
import { useEffect, useRef } from "react";

// Setter sidens <title> mens komponenten vises, og setter den forrige tilbake etterpå (U1).
export function useSideTittel(tittel) {
  useEffect(() => {
    if (!tittel) return undefined;
    const forrige = document.title;
    document.title = tittel;
    return () => {
      document.title = forrige;
    };
  }, [tittel]);
}

const FOKUSERBARE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
  'textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Dialog (U2): fokus inn når den åpnes, Tab holdes inne, Esc lukker, og fokus går
// tilbake til knappen som åpnet den. Bruk:
//   const dialogRef = useDialog(open, lukk);
//   <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="tittel-id"> …
// Første felt (eller knapp) i dialogen får fokus; marker et annet med data-autofokus.
export function useDialog(open, onClose) {
  const ref = useRef(null);
  const lukkRef = useRef(onClose);
  lukkRef.current = onClose;

  useEffect(() => {
    if (!open || !ref.current) return undefined;
    const panel = ref.current;
    const forrige = document.activeElement;
    const synlige = () =>
      [...panel.querySelectorAll(FOKUSERBARE)].filter((el) => el.getClientRects().length > 0);

    const forst =
      panel.querySelector("[data-autofokus]") ||
      panel.querySelector("input:not([disabled]), textarea:not([disabled]), select:not([disabled])") ||
      synlige()[0];
    if (forst) forst.focus();
    else {
      panel.setAttribute("tabindex", "-1");
      panel.focus();
    }

    function tast(e) {
      if (e.key === "Escape") {
        e.stopPropagation();
        lukkRef.current?.();
        return;
      }
      if (e.key !== "Tab") return;
      const liste = synlige();
      if (!liste.length) {
        e.preventDefault();
        return;
      }
      const første = liste[0];
      const siste = liste[liste.length - 1];
      if (!panel.contains(document.activeElement)) {
        e.preventDefault();
        første.focus();
      } else if (e.shiftKey && document.activeElement === første) {
        e.preventDefault();
        siste.focus();
      } else if (!e.shiftKey && document.activeElement === siste) {
        e.preventDefault();
        første.focus();
      }
    }

    document.addEventListener("keydown", tast);
    return () => {
      document.removeEventListener("keydown", tast);
      if (forrige && document.contains(forrige) && typeof forrige.focus === "function") forrige.focus();
    };
  }, [open]);

  return ref;
}

// Godkjenning med angrefrist (U2): knappen som ble trykket forsvinner eller blir
// deaktivert, så fokus må flyttes. «Angre» får fokus når den dukker opp; angrer
// kunden, går fokus tilbake til startknappen; er valget lagret, til kvitteringen.
//   const { angreRef, startRef, vedtakRef } = useAngreFokus(!!pendingDecision, harVedtak);
// vedtakRef settes på et element med tabIndex={-1}.
export function useAngreFokus(venter, harVedtak) {
  const angreRef = useRef(null);
  const startRef = useRef(null);
  const vedtakRef = useRef(null);
  const ventet = useRef(false);
  // Bare valg kunden har startet på denne siden — et vedtak som var lagret fra før,
  // skal ikke ta fokus når siden lastes.
  const startetHer = useRef(false);

  useEffect(() => {
    if (venter) {
      startetHer.current = true;
      angreRef.current?.focus();
    } else if (ventet.current && !harVedtak) startRef.current?.focus();
    ventet.current = venter;
  }, [venter, harVedtak]);

  useEffect(() => {
    const fokusBorte = !document.activeElement || document.activeElement === document.body;
    if (harVedtak && startetHer.current && fokusBorte) vedtakRef.current?.focus();
  }, [harVedtak]);

  return { angreRef, startRef, vedtakRef };
}
